import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";
import { GoogleMap, MarkerF, PolygonF } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import PageHeader from "../components/common/PageHeader";
import LocationMediaExplorer from "../components/LocationMediaExplorer";
import DrawTools from "../components/DrawTools";
import FloatingFormActions from "../components/common/FloatingFormActions";
import MapFloorplanOverlay from "../components/floorplans/MapFloorplanOverlay";

const DEFAULT_CENTER = { lat: 47.8238, lng: 13.0288 };

const TABS = [
  { id: "Identity", icon: "solar:shop-2-linear" },
  { id: "Location & Pin", icon: "solar:map-point-linear" },
  { id: "Building Outline", icon: "solar:buildings-2-linear", optional: true },
  { id: "Media & Files", icon: "solar:gallery-wide-linear", optional: true },
];

const COMMON_LEVELS = [
  "UG (Basement)",
  "EG (Ground Floor)",
  "1. OG (1st Floor)",
  "2. OG (2nd Floor)",
  "3. OG (3rd Floor)",
  "4. OG (4th Floor)",
  "Dachgeschoss (Attic / Roof)",
  "Außenbereich (Outdoor Area)",
];

const COUNTRY_CODES = [
  { code: "+49", label: "Germany (+49)" },
  { code: "+43", label: "Austria (+43)" },
  { code: "+41", label: "Switzerland (+41)" },
  { code: "+1", label: "USA / Canada (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "+33", label: "France (+33)" },
  { code: "+39", label: "Italy (+39)" },
  { code: "+34", label: "Spain (+34)" },
  { code: "+31", label: "Netherlands (+31)" },
  { code: "+32", label: "Belgium (+32)" },
];

const LocationsSubFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoaded } = useGoogleMaps();

  // Navigation & Tabs
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "Identity");
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Map references and reactive instances
  const pinMapRef = useRef(null);
  const outlineMapRef = useRef(null);
  const [pinMapInstance, setPinMapInstance] = useState(null);
  const [outlineMapInstance, setOutlineMapInstance] = useState(null);

  // Options
  const [mainLocations, setMainLocations] = useState([]);
  const [placeTypes, setPlaceTypes] = useState([]);

  // Building outline state
  const [outlineDrawVersion, setOutlineDrawVersion] = useState(0);
  const [coordStep, setCoordStep] = useState(0.00001);
  const [activePolyIndex, setActivePolyIndex] = useState(0);
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);
  const [showJsonRaw, setShowJsonRaw] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    main_location_id: "",
    name: "",
    unit_code: "",
    store_code: "",
    type: "",
    primary_category_id: "",
    additional_category: "",
    opening_date: "",
    level: "",
    building_levels: "",
    building_levels_above_ground: "",
    building_levels_below_ground: "",
    main_entrance_level: "",
    description: "",
    phone_country_code: "",
    phone_number: "",
    email: "",
    website_url: "",
    street_address: "",
    house_number: "",
    postal_code: "",
    city: "",
    district: "",
    state: "",
    country: "",
    lat: "",
    lng: "",
    geometrics_outline: "",
    status: "ACTIVE",
  });

  const [initialData, setInitialData] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [topLocations, setTopLocations] = useState([]);
  const [showCustomLevelInput, setShowCustomLevelInput] = useState(false);

  // Gemini AI State for Description
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAction, setAiAction] = useState(null);
  const [previousDescription, setPreviousDescription] = useState(null);
  const [showAiMenu, setShowAiMenu] = useState(false);

  // Selected parent main location details for map context & reference outline
  const [parentLocation, setParentLocation] = useState(null);

  // When user selects or changes parent main location in dropdown
  const handleParentLocationChange = useCallback((mainLocId, locationsList = mainLocations) => {
    const list = locationsList.length > 0 ? locationsList : mainLocations;
    const selected = list.find((loc) => String(loc.id) === String(mainLocId) || String(loc.location_id) === String(mainLocId));
    setFormData((prev) => {
      const next = { ...prev, main_location_id: mainLocId };
      if (selected) {
        next.building_levels = selected.building_levels || "";
        next.building_levels_above_ground = selected.building_levels_above_ground || "";
        next.building_levels_below_ground = selected.building_levels_below_ground || "";
        next.main_entrance_level = selected.main_entrance_level || "";
      }
      // If no coords yet or changing parent on new unit, adopt parent location coords as starting pin
      if (selected?.lat && selected?.lng && (!id || !prev.lat || !prev.lng)) {
        next.lat = String(selected.lat);
        next.lng = String(selected.lng);
      }
      if (selected) {
        if (selected.building_levels) next.building_levels = selected.building_levels;
        if (selected.building_levels_above_ground) next.building_levels_above_ground = selected.building_levels_above_ground;
        if (selected.building_levels_below_ground) next.building_levels_below_ground = selected.building_levels_below_ground;
        if (selected.main_entrance_level) next.main_entrance_level = selected.main_entrance_level;
      }
      return next;
    });

    if (selected) {
      setParentLocation({
        id: selected.id,
        name: selected.location_name || selected.name,
        location_name: selected.location_name || selected.name,
        lat: selected.lat,
        lng: selected.lng,
        geometrics_outline: selected.geometrics_outline,
        address: [selected.street_address, selected.house_number, selected.city].filter(Boolean).join(" "),
        street_address: selected.street_address || "",
        house_number: selected.house_number || "",
        postal_code: selected.postal_code || "",
        city: selected.city || "",
        building_levels: selected.building_levels || "",
        building_levels_above_ground: selected.building_levels_above_ground || "",
        building_levels_below_ground: selected.building_levels_below_ground || "",
        main_entrance_level: selected.main_entrance_level || "",
      });
      // Pan pin map to parent location
      if (pinMapRef.current && selected.lat && selected.lng) {
        pinMapRef.current.panTo({ lat: Number(selected.lat), lng: Number(selected.lng) });
        pinMapRef.current.setZoom(19);
      }
    } else {
      setParentLocation(null);
    }
  }, [id, mainLocations]);

  // Active parent location with resolved metadata and building levels
  const activeParentLocation = useMemo(() => {
    const parentId = formData.main_location_id;
    if (!parentId) return parentLocation || null;
    const matched = mainLocations.find(
      (loc) => String(loc.id) === String(parentId) || String(loc.location_id) === String(parentId)
    );
    if (matched) {
      return {
        ...parentLocation,
        ...matched,
        name: matched.location_name || matched.name || parentLocation?.name,
        location_name: matched.location_name || matched.name || parentLocation?.name,
        top_location_id: matched.top_location_id ?? parentLocation?.top_location_id,
        building_levels: matched.building_levels ?? parentLocation?.building_levels,
        building_levels_above_ground: matched.building_levels_above_ground ?? parentLocation?.building_levels_above_ground,
        building_levels_below_ground: matched.building_levels_below_ground ?? parentLocation?.building_levels_below_ground,
        main_entrance_level: matched.main_entrance_level ?? parentLocation?.main_entrance_level,
      };
    }
    return parentLocation || null;
  }, [formData.main_location_id, mainLocations, parentLocation]);

  // Selected Top Location and its preset categories
  const selectedTopLocation = useMemo(() => {
    const topId = activeParentLocation?.top_location_id;
    if (topId && topLocations.length > 0) {
      const match = topLocations.find((tl) => String(tl.id) === String(topId));
      if (match) return match;
    }
    if (activeParentLocation?.top_location_name || activeParentLocation?.parent_top_location_name) {
      return {
        id: topId,
        name: activeParentLocation.top_location_name || activeParentLocation.parent_top_location_name,
        preset_sublocation_categories: activeParentLocation.top_location_preset_sublocation_categories || activeParentLocation.parent_top_location_preset_sublocation_categories,
        preset_categories: activeParentLocation.top_location_preset_categories || activeParentLocation.parent_top_location_preset_categories,
      };
    }
    return null;
  }, [activeParentLocation, topLocations]);

  const topLocationPresetCategories = useMemo(() => {
    // Look first for sublocation-specific presets from top location
    let val = selectedTopLocation?.preset_sublocation_categories;
    // Fallback to general preset_categories if no sublocation presets specified
    if (!val || (Array.isArray(val) && val.length === 0)) {
      val = selectedTopLocation?.preset_categories;
    }
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return typeof val === "string" ? val.split(",").map((s) => s.trim()).filter(Boolean) : [];
    }
  }, [selectedTopLocation]);

  const { presetCategories, otherCategories } = useMemo(() => {
    if (!topLocationPresetCategories || topLocationPresetCategories.length === 0) {
      return { presetCategories: [], otherCategories: categoryOptions };
    }

    const presetMatches = [];
    const others = [];

    categoryOptions.forEach((cat) => {
      const isPreset = topLocationPresetCategories.some(
        (p) =>
          p === cat.label_de ||
          p === cat.label ||
          p === cat.type_key ||
          (cat.label_de && p.toLowerCase() === cat.label_de.toLowerCase()) ||
          (cat.label && p.toLowerCase() === cat.label.toLowerCase()) ||
          (cat.type_key && p.toLowerCase() === cat.type_key.toLowerCase())
      );
      if (isPreset) {
        presetMatches.push(cat);
      } else {
        others.push(cat);
      }
    });

    return { presetCategories: presetMatches, otherCategories: others };
  }, [categoryOptions, topLocationPresetCategories]);

  // Dynamically compute available levels from parent main location
  const levelOptions = useMemo(() => {
    const parent = activeParentLocation;
    const aboveRaw = parent?.building_levels_above_ground;
    const belowRaw = parent?.building_levels_below_ground;
    const entranceRaw = parent?.main_entrance_level;
    const legacyTotalRaw = parent?.building_levels;

    const aboveNum = aboveRaw !== undefined && aboveRaw !== null && aboveRaw !== "" ? parseInt(aboveRaw, 10) : null;
    const belowNum = belowRaw !== undefined && belowRaw !== null && belowRaw !== "" ? parseInt(belowRaw, 10) : null;
    const legacyTotalNum = legacyTotalRaw !== undefined && legacyTotalRaw !== null && legacyTotalRaw !== "" ? parseInt(legacyTotalRaw, 10) : null;

    const getEntranceSuffix = (lvlVal) => {
      if (entranceRaw !== undefined && entranceRaw !== null && entranceRaw !== "" && String(entranceRaw) === String(lvlVal)) {
        return " - Main Entrance";
      }
      return "";
    };

    if (aboveNum !== null || belowNum !== null) {
      const items = [];

      // Upper floors: from (aboveNum - 1) down to 1 (ground floor 0 counts as level 1 above ground)
      if (aboveNum !== null && !isNaN(aboveNum) && aboveNum > 1) {
        for (let i = aboveNum - 1; i >= 1; i--) {
          items.push({
            value: String(i),
            label: `${i} (${i}. Floor / OG${getEntranceSuffix(i)})`,
          });
        }
      }

      // Ground floor: 0
      items.push({
        value: "0",
        label: `0 (Ground Floor / EG${getEntranceSuffix("0")})`,
      });

      // Basement floors: from -1 down to belowNum
      if (belowNum !== null && !isNaN(belowNum) && belowNum !== 0) {
        const minBelow = belowNum < 0 ? belowNum : -Math.abs(belowNum);
        for (let i = -1; i >= minBelow; i--) {
          items.push({
            value: String(i),
            label: `${i} (${Math.abs(i)}. Basement / UG${getEntranceSuffix(i)})`,
          });
        }
      }

      return items;
    }

    if (legacyTotalNum !== null && !isNaN(legacyTotalNum) && legacyTotalNum > 0) {
      const items = [];
      for (let i = legacyTotalNum - 1; i >= 1; i--) {
        items.push({
          value: String(i),
          label: `${i} (${i}. Floor / OG${getEntranceSuffix(i)})`,
        });
      }
      items.push({
        value: "0",
        label: `0 (Ground Floor / EG${getEntranceSuffix("0")})`,
      });
      return items;
    }

    // Default fallback if parent location has not yet defined building levels
    const fallbackItems = [];
    for (let i = 5; i >= 1; i--) {
      fallbackItems.push({
        value: String(i),
        label: `${i} (${i}. Floor / OG)`,
      });
    }
    fallbackItems.push({
      value: "0",
      label: "0 (Ground Floor / EG)",
    });
    for (let i = -1; i >= -3; i--) {
      fallbackItems.push({
        value: String(i),
        label: `${i} (${Math.abs(i)}. Basement / UG)`,
      });
    }
    return fallbackItems;
  }, [activeParentLocation]);

  // Handle Gemini AI Description Generation / Enhancement for Sublocation
  const handleAiDescription = async (action = 'generate', language = 'de', tone = 'engaging') => {
    const subName = (formData.name || formData.location_name || '').trim();
    const parentName = (
      parentLocation?.name ||
      parentLocation?.location_name ||
      activeParentLocation?.name ||
      activeParentLocation?.location_name ||
      ''
    ).trim();

    if (action === 'generate' && !subName && !parentName) {
      showToast('error', 'Please enter a Unit Name or select a Parent Business before generating.');
      return;
    }

    if ((action === 'polish' || action === 'shorten' || action.startsWith('translate')) && !formData.description?.trim()) {
      showToast('warning', 'Please enter or generate a description first.');
      return;
    }

    // Resolve category labels for human-friendly Gemini prompt
    const primaryCatVal = formData.primary_category_id || formData.type || '';
    const catObj = categoryOptions.find(
      (c) =>
        String(c.id) === String(primaryCatVal) ||
        c.type_key === primaryCatVal ||
        c.label_de === primaryCatVal ||
        c.label === primaryCatVal
    );
    const primaryCatLabel = catObj ? (catObj.label_de || catObj.label || catObj.type_key) : primaryCatVal;

    const secCatVal = formData.additional_category || '';
    const secCatObj = categoryOptions.find(
      (c) =>
        String(c.id) === String(secCatVal) ||
        c.type_key === secCatVal ||
        c.label_de === secCatVal ||
        c.label === secCatVal
    );
    const secondaryCatLabel = secCatObj ? (secCatObj.label_de || secCatObj.label || secCatObj.type_key) : secCatVal;

    setAiLoading(true);
    setAiAction(action);
    try {
      const response = await api.post('/ai/generate-description', {
        name: subName || parentName,
        is_sublocation: true,
        parent_name: parentName,
        level: formData.level,
        primary_category: primaryCatLabel,
        secondary_category: secondaryCatLabel,
        street_address: formData.street_address || parentLocation?.street_address,
        house_number: formData.house_number || parentLocation?.house_number,
        postal_code: formData.postal_code || parentLocation?.postal_code,
        city: formData.city || parentLocation?.city,
        website: formData.website_url,
        phone: formData.phone_number,
        current_description: formData.description,
        action,
        language,
        tone
      });

      if (response.data?.success && response.data?.description) {
        setPreviousDescription(formData.description);
        setFormData(prev => ({
          ...prev,
          description: response.data.description
        }));

        const actionLabels = {
          generate: 'Description generated with Gemini!',
          polish: 'Description polished and refined!',
          shorten: 'Description condensed!',
          translate_en: 'Translated to English!',
          translate_de: 'Translated to German!'
        };
        showToast('success', actionLabels[action] || 'Description updated by Gemini AI');
      } else {
        showToast('error', response.data?.error || 'Failed to generate description');
      }
    } catch (err) {
      console.error('AI Description Generation error:', err);
      const errMsg = err.response?.data?.error || err.message || 'Error contacting Gemini AI';
      showToast('error', errMsg);
    } finally {
      setAiLoading(false);
      setAiAction(null);
    }
  };

  // Quick helper to adopt parent business address
  const handleAdoptParentAddress = () => {
    if (!parentLocation) {
      showToast("error", "No parent location selected.");
      return;
    }
    const matched = mainLocations.find(
      (loc) => String(loc.id) === String(formData.main_location_id) || String(loc.location_id) === String(formData.main_location_id)
    );
    if (!matched) return;
    setFormData((prev) => ({
      ...prev,
      street_address: matched.street_address || prev.street_address,
      house_number: matched.house_number || prev.house_number,
      postal_code: matched.postal_code || prev.postal_code,
      city: matched.city || prev.city,
    }));
    showToast("info", "Copied address from parent business.");
  };

  // Fetch Parent Main Locations, Place Types, & Categories Options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [mainLocRes, typesRes, topLocRes] = await Promise.all([
          api.get("/options/main-locations"),
          api.get("/options/sublocation-types"),
          api.get("/options/top-locations").catch(() => ({ data: [] })),
        ]);
        const fetchedMainLocations = mainLocRes.data || [];
        setMainLocations(fetchedMainLocations);
        setPlaceTypes(typesRes.data || []);
        setCategoryOptions(typesRes.data || []);
        setTopLocations(topLocRes.data || []);
      } catch (err) {
        console.error("Error loading options:", err);
      }
    };
    fetchOptions();
  }, []);

  // Fetch Existing Sub-Location Data if Editing
  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      if (!formData.name) {
        setLoading(true);
      }
      try {
        const res = await api.get(`/sub-locations/${id}`);
        if (res.data?.success && res.data.data) {
          const item = res.data.data;
          const loadedData = {
            main_location_id: item.main_location_id ? String(item.main_location_id) : "",
            name: item.name || "",
            unit_code: item.unit_code || item.store_code || "",
            store_code: item.store_code || item.unit_code || "",
            type: item.type || item.primary_category_id || "",
            primary_category_id: item.primary_category_id || item.type || "",
            additional_category: item.additional_category || "",
            opening_date: item.opening_date ? item.opening_date.split("T")[0] : "",
            level: item.level || "",
            building_levels: item.building_levels || "",
            building_levels_above_ground: item.building_levels_above_ground || "",
            building_levels_below_ground: item.building_levels_below_ground || "",
            main_entrance_level: item.main_entrance_level || "",
            description: item.description || "",
            phone_country_code: item.phone_country_code || "",
            phone_number: item.phone_number || "",
            email: item.email || "",
            website_url: item.website_url || "",
            street_address: item.street_address || "",
            house_number: item.house_number || "",
            postal_code: item.postal_code || "",
            city: item.city || "",
            district: item.district || "",
            state: item.state || "",
            country: item.country || "",
            lat: item.lat !== null && item.lat !== undefined ? String(item.lat) : "",
            lng: item.lng !== null && item.lng !== undefined ? String(item.lng) : "",
            geometrics_outline: item.geometrics_outline || "",
            status: item.status || "ACTIVE",
          };
          setFormData(loadedData);
          setInitialData(loadedData);

          // Parent location context
          setParentLocation({
            id: item.main_location_id,
            name: item.parent_main_name,
            top_location_id: item.parent_top_location_id || null,
            lat: item.parent_lat,
            lng: item.parent_lng,
            geometrics_outline: item.parent_geometrics_outline,
            address: [item.parent_street_address, item.parent_house_number, item.parent_city].filter(Boolean).join(" "),
            street_address: item.parent_street_address || "",
            house_number: item.parent_house_number || "",
            postal_code: item.parent_postal_code || "",
            city: item.parent_city || "",
            building_levels: item.parent_building_levels || item.building_levels || "",
            building_levels_above_ground: item.parent_building_levels_above_ground || item.building_levels_above_ground || "",
            building_levels_below_ground: item.parent_building_levels_below_ground || item.building_levels_below_ground || "",
            main_entrance_level: item.parent_main_entrance_level || item.main_entrance_level || "",
          });
        }
      } catch (err) {
        console.error("Error fetching sub-location:", err);
        showToast("error", "Failed to load sub-location data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const resolveCategoryValue = useCallback((val) => {
    if (!val) return "";
    const match = categoryOptions.find(
      (c) =>
        c.label_de === val ||
        c.type_key === val ||
        c.label?.toLowerCase() === val?.toLowerCase() ||
        String(c.id) === String(val)
    );
    return match ? (match.label_de || match.label || match.type_key) : val;
  }, [categoryOptions]);

  // Helper to parse polygon GeoJSON / format
  const parsePolygons = useCallback((geoJson) => {
    if (!geoJson) return [];
    try {
      const parsed = typeof geoJson === "string" ? JSON.parse(geoJson) : geoJson;
      if (Array.isArray(parsed) && parsed.length && parsed[0].path) {
        return parsed.map((p) => (p.path || []).map((pt) => ({ lat: Number(pt.lat) || 0, lng: Number(pt.lng) || 0 })));
      }
      if (Array.isArray(parsed) && parsed.every((p) => Array.isArray(p))) {
        return parsed.filter((p) => p.length).map((poly) => poly.map((pt) => ({ lat: pt.lat !== undefined ? Number(pt.lat) : 0, lng: pt.lng !== undefined ? Number(pt.lng) : 0 })));
      }
      if (Array.isArray(parsed) && parsed.length && parsed[0].lat !== undefined) {
        return [parsed.map((pt) => ({ lat: pt.lat !== undefined ? Number(pt.lat) : 0, lng: pt.lng !== undefined ? Number(pt.lng) : 0 }))];
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  // Polygons for the sublocation outline
  const activePolys = parsePolygons(formData.geometrics_outline);
  const activeIndex = Math.min(activePolyIndex, Math.max(0, activePolys.length - 1));
  const currentPoints = activePolys[activeIndex] || [];

  // Polygons for the parent main location (used as reference on map)
  const parentOutlinePolygons = parsePolygons(parentLocation?.geometrics_outline);

  // Level floorplans from parent main location
  const [parentFloorplans, setParentFloorplans] = useState([]);
  const [showFloorplanOverlay, setShowFloorplanOverlay] = useState(true);
  const [floorplanOpacity, setFloorplanOpacity] = useState(0.85);

  // Fetch parent floorplans when main_location_id changes
  useEffect(() => {
    const parentId = formData.main_location_id;
    if (!parentId) {
      setParentFloorplans([]);
      return;
    }

    let isMounted = true;
    const fetchParentFloorplans = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`/api/main-locations/${parentId}/floorplans`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (isMounted && data.success && Array.isArray(data.data)) {
          setParentFloorplans(data.data);
        }
      } catch (err) {
        console.warn("Could not fetch level floorplans for sublocation:", err);
      }
    };

    fetchParentFloorplans();
    return () => { isMounted = false; };
  }, [formData.main_location_id]);

  // Derived active floorplan matching selected formData.level
  const activeFloorplan = useMemo(() => {
    if (formData.level === undefined || formData.level === null || formData.level === "" || !parentFloorplans.length) {
      return null;
    }
    return (
      parentFloorplans.find(
        (p) => String(p.level).trim() === String(formData.level).trim()
      ) || null
    );
  }, [formData.level, parentFloorplans]);

  // Fit map to active floorplan bounds
  const handleFitFloorplan = useCallback((targetMap) => {
    const map = targetMap || pinMapInstance || outlineMapInstance;
    if (!map || !activeFloorplan || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(parseFloat(activeFloorplan.south), parseFloat(activeFloorplan.west)),
      new window.google.maps.LatLng(parseFloat(activeFloorplan.north), parseFloat(activeFloorplan.east))
    );
    map.fitBounds(bounds, 30);
  }, [activeFloorplan, pinMapInstance, outlineMapInstance]);

  // Tab completion status
  const isTabCompleted = (tabId) => {
    switch (tabId) {
      case "Identity":
        return Boolean(formData.main_location_id && formData.name);
      case "Location & Pin":
        return Boolean(formData.lat && formData.lng);
      case "Building Outline":
        return currentPoints.length >= 3;
      case "Media & Files":
        return Boolean(id);
      default:
        return false;
    }
  };

  // Check if form has unsaved changes
  const hasFormChanged = () => {
    if (!initialData) {
      return Boolean(
        formData.main_location_id || 
        formData.name || 
        formData.unit_code || 
        formData.store_code || 
        formData.type || 
        formData.level || 
        formData.lat || 
        formData.geometrics_outline
      );
    }
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  };

  // Map click handler to place pin
  const handlePinMapClick = (e) => {
    if (!e.latLng) return;
    const newLat = e.latLng.lat().toFixed(7);
    const newLng = e.latLng.lng().toFixed(7);
    setFormData((prev) => ({ ...prev, lat: String(newLat), lng: String(newLng) }));
  };

  // Marker drag end handler
  const handleMarkerDragEnd = (e) => {
    if (!e.latLng) return;
    const newLat = e.latLng.lat().toFixed(7);
    const newLng = e.latLng.lng().toFixed(7);
    setFormData((prev) => ({ ...prev, lat: String(newLat), lng: String(newLng) }));
  };

  // Snap pin to parent location center
  const handleSnapToParent = () => {
    if (parentLocation?.lat && parentLocation?.lng) {
      setFormData((prev) => ({
        ...prev,
        lat: String(Number(parentLocation.lat).toFixed(7)),
        lng: String(Number(parentLocation.lng).toFixed(7)),
      }));
      if (pinMapRef.current) {
        pinMapRef.current.panTo({ lat: Number(parentLocation.lat), lng: Number(parentLocation.lng) });
        pinMapRef.current.setZoom(19);
      }
    } else {
      showToast("info", "Please select a parent business first.");
    }
  };

  // Boundary fitting for Outline Map
  const fitMapToBoundary = useCallback((outlineData) => {
    if (!outlineData || !outlineMapRef.current) return;
    try {
      const parsed = typeof outlineData === "string" ? JSON.parse(outlineData) : outlineData;
      if (!Array.isArray(parsed)) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasCoords = false;

      parsed.forEach((item) => {
        const path = Array.isArray(item) ? item : item.path;
        if (Array.isArray(path)) {
          path.forEach((pt) => {
            if (pt.lat && pt.lng) {
              bounds.extend({ lat: parseFloat(pt.lat), lng: parseFloat(pt.lng) });
              hasCoords = true;
            }
          });
        }
      });

      if (hasCoords) {
        outlineMapRef.current.fitBounds(bounds, 50);
      }
    } catch (e) {
      console.warn("Boundary zoom failed:", e);
    }
  }, []);

  const onOutlineMapLoad = useCallback(
    (map) => {
      outlineMapRef.current = map;
      setOutlineMapInstance(map);
      try {
        map.setTilt(0);
        map.setMapTypeId("roadmap");
      } catch (e) {}

      if (formData.lat && formData.lng) {
        map.panTo({ lat: Number(formData.lat), lng: Number(formData.lng) });
        map.setZoom(19);
      } else if (parentLocation?.lat && parentLocation?.lng) {
        map.panTo({ lat: Number(parentLocation.lat), lng: Number(parentLocation.lng) });
        map.setZoom(19);
      }

      if (formData.geometrics_outline) {
        setTimeout(() => fitMapToBoundary(formData.geometrics_outline), 300);
      } else if (parentLocation?.geometrics_outline) {
        setTimeout(() => fitMapToBoundary(parentLocation.geometrics_outline), 300);
      }
    },
    [formData.lat, formData.lng, formData.geometrics_outline, parentLocation, fitMapToBoundary]
  );

  // Outline vertex manipulation
  const handleCoordinateChange = (polyIdx, ptIdx, field, val) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const updatedPolys = polys.map((poly, pIdx) => {
      if (pIdx !== polyIdx) return poly;
      return poly.map((pt, idx) => {
        if (idx !== ptIdx) return pt;
        return { ...pt, [field]: val };
      });
    });

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys),
    }));
  };

  const adjustCoordinate = (polyIdx, ptIdx, field, delta) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const currentVal = parseFloat(polys[polyIdx][ptIdx][field]) || 0;
    const newVal = Number((currentVal + delta).toFixed(7));

    const updatedPolys = polys.map((poly, pIdx) => {
      if (pIdx !== polyIdx) return poly;
      return poly.map((pt, idx) => {
        if (idx !== ptIdx) return pt;
        return { ...pt, [field]: newVal };
      });
    });

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys),
    }));
  };

  const handleRemovePoint = (polyIdx, ptIdx) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx]) return;

    const updatedPath = polys[polyIdx].filter((_, idx) => idx !== ptIdx);
    let updatedPolys;
    if (updatedPath.length === 0) {
      updatedPolys = polys.filter((_, idx) => idx !== polyIdx);
    } else {
      updatedPolys = polys.map((p, idx) => (idx === polyIdx ? updatedPath : p));
    }

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys),
    }));
  };

  const handleAddStarterOutline = () => {
    const baseLat = Number(formData.lat) || (parentLocation?.lat ? Number(parentLocation.lat) : DEFAULT_CENTER.lat);
    const baseLng = Number(formData.lng) || (parentLocation?.lng ? Number(parentLocation.lng) : DEFAULT_CENTER.lng);
    const d = 0.00008; // ~8 meters box

    const starterBox = [
      { lat: Number((baseLat + d).toFixed(7)), lng: Number((baseLng - d).toFixed(7)) },
      { lat: Number((baseLat + d).toFixed(7)), lng: Number((baseLng + d).toFixed(7)) },
      { lat: Number((baseLat - d).toFixed(7)), lng: Number((baseLng + d).toFixed(7)) },
      { lat: Number((baseLat - d).toFixed(7)), lng: Number((baseLng - d).toFixed(7)) },
    ];

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify([starterBox]),
    }));
    setOutlineDrawVersion((v) => v + 1);
  };

  // Add an additional shape (polygon)
  const handleAddShape = () => {
    const polys = parsePolygons(formData.geometrics_outline);

    const baseLat = Number(formData.lat) || (parentLocation?.lat ? Number(parentLocation.lat) : DEFAULT_CENTER.lat);
    const baseLng = Number(formData.lng) || (parentLocation?.lng ? Number(parentLocation.lng) : DEFAULT_CENTER.lng);

    let centerLat = baseLat;
    let centerLng = baseLng;

    if (outlineMapRef.current?.getCenter) {
      const c = outlineMapRef.current.getCenter();
      if (c) {
        centerLat = c.lat();
        centerLng = c.lng();
      }
    }

    const offset = polys.length * 0.00015;
    const d = 0.00008; // ~8m box

    const newBox = [
      { lat: Number((centerLat + offset + d).toFixed(7)), lng: Number((centerLng + offset - d).toFixed(7)) },
      { lat: Number((centerLat + offset + d).toFixed(7)), lng: Number((centerLng + offset + d).toFixed(7)) },
      { lat: Number((centerLat + offset - d).toFixed(7)), lng: Number((centerLng + offset + d).toFixed(7)) },
      { lat: Number((centerLat + offset - d).toFixed(7)), lng: Number((centerLng + offset - d).toFixed(7)) },
    ];

    const updatedPolys = [...polys, newBox];
    const newIndex = updatedPolys.length - 1;

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys),
    }));
    setActivePolyIndex(newIndex);
    setSelectedVertexIndex(0);
    setOutlineDrawVersion((v) => v + 1);

    if (outlineMapRef.current) {
      outlineMapRef.current.panTo({ lat: centerLat + offset, lng: centerLng + offset });
    }
  };

  const handleClearBoundary = () => {
    setFormData((prev) => ({ ...prev, geometrics_outline: "" }));
    setOutlineDrawVersion((v) => v + 1);
  };

  // Submit / Save Sublocation
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.main_location_id) {
      showToast("error", "Please select a parent business.");
      setActiveTab("Identity");
      return;
    }

    if (!formData.name.trim()) {
      showToast("error", "Please enter a unit name.");
      setActiveTab("Identity");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        main_location_id: parseInt(formData.main_location_id, 10),
        name: formData.name.trim(),
        unit_code: formData.unit_code?.trim() || null,
        store_code: formData.unit_code?.trim() || formData.store_code?.trim() || null,
        type: formData.primary_category_id || formData.type || null,
        primary_category_id: formData.primary_category_id || formData.type || null,
        additional_category: formData.additional_category || null,
        opening_date: formData.opening_date || null,
        description: formData.description || null,
        phone_country_code: formData.phone_country_code || null,
        phone_number: formData.phone_number || null,
        email: formData.email || null,
        website_url: formData.website_url || null,
        street_address: formData.street_address || null,
        house_number: formData.house_number || null,
        postal_code: formData.postal_code || null,
        city: formData.city || null,
        district: formData.district || null,
        state: formData.state || null,
        country: formData.country || null,
        lat: formData.lat ? parseFloat(formData.lat) : null,
        lng: formData.lng ? parseFloat(formData.lng) : null,
        level: formData.level || null,
        building_levels: formData.building_levels || null,
        building_levels_above_ground: formData.building_levels_above_ground || null,
        building_levels_below_ground: formData.building_levels_below_ground || null,
        main_entrance_level: formData.main_entrance_level || null,
        geometrics_outline: formData.geometrics_outline || null,
        status: formData.status || "ACTIVE",
      };

      if (id) {
        await api.put(`/sub-locations/${id}`, payload);
        showToast("success", "Sub-location updated successfully!");
        setInitialData({ ...formData });
      } else {
        const res = await api.post("/sub-locations", payload);
        showToast("success", "Sub-location created successfully!");
        setInitialData({ ...formData });
        const currentTab = activeTab;
        navigate(`/locations-sub/${res.data.id}/edit`, { replace: true, state: { activeTab: currentTab } });
      }
    } catch (err) {
      console.error("Save error:", err);
      showToast("error", err.response?.data?.message || "Failed to save sub-location.");
    } finally {
      setSaveLoading(false);
    }
  };

  // Quick save outline button inside outline tab
  const handleSaveOutline = async () => {
    if (!id) {
      handleSubmit();
      return;
    }
    setSaveLoading(true);
    try {
      await api.put(`/sub-locations/${id}`, {
        geometrics_outline: formData.geometrics_outline || null,
      });
      showToast("success", "Building outline saved!");
      setInitialData((prev) => ({ ...prev, geometrics_outline: formData.geometrics_outline }));
    } catch (err) {
      showToast("error", "Failed to save outline.");
    } finally {
      setSaveLoading(false);
    }
  };

  // Map center calculation
  const mapCenter = {
    lat: Number(formData.lat) || (parentLocation?.lat ? Number(parentLocation.lat) : DEFAULT_CENTER.lat),
    lng: Number(formData.lng) || (parentLocation?.lng ? Number(parentLocation.lng) : DEFAULT_CENTER.lng),
  };

  // Tab Walk Through Navigation
  const tabIndex = TABS.findIndex((t) => t.id === activeTab);
  const prevTab = tabIndex > 0 ? TABS[tabIndex - 1].id : null;
  const nextTab = tabIndex < TABS.length - 1 ? TABS[tabIndex + 1].id : null;

  return (
    <div className="locations-sub-form-page position-relative pb-80">
      {/* Page Header */}
      <PageHeader
        title={id ? `Edit Unit: ${formData.name || "Sub Location"}` : "Add Sub Location"}
        subtitle="Master Data Management"
        breadcrumbLinks={[
          { label: "Dashboard", url: "/" },
          { label: "Sub Locations", url: "/locations-sub" },
          { label: id ? "Edit Unit" : "Add Unit", active: true },
        ]}
      />

      <form onSubmit={handleSubmit} noValidate>
        <div className="row g-4 mt-2">
        {/* Left Side: Navigation Tabs */}
        <div className="col-xl-3 col-lg-4">
          <div className="card border radius-12 p-16 shadow-none position-sticky" style={{ top: "80px" }}>
            <div className="d-flex align-items-center gap-3 pb-16 mb-16 border-bottom">
              <div className="w-40-px h-40-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center">
                <Icon icon="solar:shop-2-bold" className="text-xl" />
              </div>
              <div className="overflow-hidden">
                <h6 className="text-sm fw-bold mb-0 text-truncate">
                  {formData.name || "New Sub-Location"}
                </h6>
                <span className="text-xs text-secondary-light">
                  {parentLocation?.name ? `Parent: ${parentLocation.name}` : "Sub Location / Unit"}
                </span>
              </div>
            </div>

            <div className="nav flex-column gap-2" role="tablist">
              {TABS.map((tab) => {
                const completed = isTabCompleted(tab.id);
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`nav-link text-start radius-8 px-14 py-10 d-flex align-items-center justify-content-between border-0 transition-all ${
                      isActive
                        ? "bg-primary-600 text-white fw-semibold shadow-xs"
                        : "text-secondary-light bg-hover-neutral-100"
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon={tab.icon} className="text-lg" />
                      <span className="text-xs">{tab.id}</span>
                      {tab.optional && (
                        <span
                          className={`badge text-xxs px-6 py-1 radius-4 ms-1 ${
                            isActive ? "bg-white-20 text-white" : "bg-neutral-100 text-secondary-light"
                          }`}
                        >
                          Optional
                        </span>
                      )}
                    </div>
                    {completed ? (
                      <Icon
                        icon="solar:check-circle-bold"
                        className={`text-sm ${isActive ? "text-white" : "text-success-main"}`}
                      />
                    ) : (
                      <span
                        className={`w-6-px h-6-px radius-circle ${
                          isActive ? "bg-white-50" : "bg-neutral-300"
                        }`}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Quick Context Summary Box */}
            {parentLocation && (
              <div className="mt-20 pt-16 border-top">
                <div className="d-flex align-items-center gap-2 mb-8">
                  <Icon icon="solar:info-circle-linear" className="text-primary-600 text-sm" />
                  <span className="text-xs fw-semibold text-dark">Parent Location</span>
                </div>
                <div className="bg-neutral-50 p-10 radius-8 border text-xs text-secondary-light">
                  <div className="fw-medium text-dark mb-1">{parentLocation.name}</div>
                  {parentLocation.address && <div>{parentLocation.address}</div>}
                  {parentLocation.lat && parentLocation.lng && (
                    <div className="font-monospace mt-1 text-primary-600" style={{ fontSize: "11px" }}>
                      {Number(parentLocation.lat).toFixed(5)}, {Number(parentLocation.lng).toFixed(5)}
                    </div>
                  )}
                  {parentOutlinePolygons.length > 0 && (
                    <div className="badge bg-primary-subtle text-primary-700 radius-4 mt-2 py-1 px-2 text-xxs">
                      ✓ Building Outline Available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Content Area: Active Tab View */}
        <div className="col-xxl-9 col-lg-8">
          {/* TAB 1: IDENTITY */}
            {activeTab === "Identity" && (
              <div className="card border radius-12 p-24 shadow-none">
                <div className="border-bottom pb-16 mb-20">
                  <h6 className="text-md fw-bold mb-1">Unit Identity & Master Details</h6>
                  <p className="text-xs text-secondary-light mb-0">
                    Define the core business context, parent business association, category, and floor level.
                  </p>
                </div>

                <div className="row g-3">
                  {/* Parent Business / Main Location */}
                  <div className="col-md-8">
                    <label className="form-label text-xs fw-semibold text-dark mb-1">
                      Parent Business <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select radius-8 text-xs py-10"
                      value={
                        mainLocations.find(
                          (loc) =>
                            String(loc.location_id) === String(formData.main_location_id) ||
                            String(loc.id) === String(formData.main_location_id)
                        )
                          ? String(
                              mainLocations.find(
                                (loc) =>
                                  String(loc.location_id) === String(formData.main_location_id) ||
                                  String(loc.id) === String(formData.main_location_id)
                              ).location_id ||
                              mainLocations.find(
                                (loc) =>
                                  String(loc.location_id) === String(formData.main_location_id) ||
                                  String(loc.id) === String(formData.main_location_id)
                              ).id
                            )
                          : formData.main_location_id
                      }
                      onChange={(e) => handleParentLocationChange(e.target.value)}
                      required
                    >
                      <option value="">-- Select Parent Business --</option>
                      {mainLocations.map((loc) => {
                        const optVal = String(loc.location_id || loc.id);
                        return (
                          <option key={optVal} value={optVal}>
                            {loc.location_name || loc.name}
                            {loc.city ? ` (${loc.city})` : ""}
                          </option>
                        );
                      })}
                    </select>
                    {activeParentLocation && (
                      <span className="text-success text-xxs mt-1 d-block">
                        Linked to: <strong>{activeParentLocation.name || activeParentLocation.location_name}</strong>
                        {activeParentLocation.street_address && ` • ${activeParentLocation.street_address}`}
                        {activeParentLocation.city && ` ${activeParentLocation.city}`}
                      </span>
                    )}
                  </div>

                  {/* Unit Code */}
                  <div className="col-md-4">
                    <label className="form-label text-xs fw-semibold text-dark mb-1">
                      Unit Code
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-xs py-10 font-monospace"
                      placeholder="e.g. U-101, Shop 4B"
                      value={formData.unit_code}
                      onChange={(e) => setFormData({ ...formData, unit_code: e.target.value, store_code: e.target.value })}
                    />
                    <span className="text-secondary-light text-xxs mt-1 d-block">
                      Optional unique unit or booth identifier.
                    </span>
                  </div>

                  {/* Unit Name */}
                  <div className="col-md-12">
                    <label className="form-label text-xs fw-semibold text-dark mb-1">
                      Unit / Sub Location Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 text-xs py-10"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  {/* Primary Category / Type */}
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-semibold text-dark mb-1 d-flex align-items-center justify-content-between">
                      <span>Primary Category / Type</span>
                      {presetCategories.length > 0 && (
                        <span className="text-warning-700 text-2xs fw-normal d-inline-flex align-items-center gap-1">
                          <span className="text-warning-600">★</span> Presets prioritized
                        </span>
                      )}
                    </label>
                    <select
                      className="form-select radius-8 text-xs py-10"
                      name="primary_category_id"
                      value={resolveCategoryValue(formData.primary_category_id || formData.type)}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          primary_category_id: val,
                          type: val,
                        }));
                      }}
                    >
                      <option value="">-- Select Primary Category --</option>
                      {(formData.primary_category_id || formData.type) &&
                        !categoryOptions.some(
                          (c) =>
                            c.label_de === resolveCategoryValue(formData.primary_category_id || formData.type) ||
                            c.type_key === (formData.primary_category_id || formData.type) ||
                            c.label === (formData.primary_category_id || formData.type)
                        ) && (
                          <option value={formData.primary_category_id || formData.type}>
                            {formData.primary_category_id || formData.type}
                          </option>
                      )}
                      {presetCategories.length > 0 ? (
                        <>
                          <optgroup label={`★ Preset Categories (${selectedTopLocation?.name || "Top Location"})`}>
                            {presetCategories.map((cat) => {
                              const val = cat.label_de || cat.label || cat.type_key;
                              return (
                                <option key={`preset-prim-${cat.id || cat.type_key}`} value={val}>
                                  ★ {cat.label_de ? `${cat.label_de} (${cat.label || cat.type_key})` : (cat.label || cat.type_key)}
                                </option>
                              );
                            })}
                          </optgroup>
                          <optgroup label="All Other Categories">
                            {otherCategories.map((cat) => {
                              const val = cat.label_de || cat.label || cat.type_key;
                              return (
                                <option key={cat.id || cat.type_key} value={val}>
                                  {cat.label_de ? `${cat.label_de} (${cat.label || cat.type_key})` : (cat.label || cat.type_key)}
                                </option>
                              );
                            })}
                          </optgroup>
                        </>
                      ) : (
                        categoryOptions.map((cat) => {
                          const val = cat.label_de || cat.label || cat.type_key;
                          return (
                            <option key={cat.id || cat.type_key} value={val}>
                              {cat.label_de ? `${cat.label_de} (${cat.label || cat.type_key})` : (cat.label || cat.type_key)}
                            </option>
                          );
                        })
                      )}
                    </select>
                  </div>

                  {/* Floor / Level */}
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label text-xs fw-semibold text-dark mb-0">
                        Floor / Level
                      </label>
                      <button
                        type="button"
                        className="btn btn-link text-primary-600 p-0 text-xs text-decoration-none"
                        onClick={() => setShowCustomLevelInput((prev) => !prev)}
                      >
                        {showCustomLevelInput ? "Pick from dropdown" : "Custom level"}
                      </button>
                    </div>

                    {showCustomLevelInput ? (
                      <input
                        type="text"
                        className="form-control radius-8 text-xs py-8"
                        value={formData.level}
                        onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                        autoFocus
                      />
                    ) : (
                      <div className="input-group">
                        <span className="input-group-text bg-neutral-50 border-end-0 text-secondary-light">
                          <Icon icon="solar:layers-linear" />
                        </span>
                        <select
                          className="form-select radius-8 border-start-0 text-xs py-8"
                          name="level"
                          value={formData.level || ""}
                          onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                        >
                          <option value="">
                            {activeParentLocation
                              ? "-- Select floor / level --"
                              : "-- Select parent location first --"}
                          </option>
                          {levelOptions.map((opt) => {
                            const hasPlan = parentFloorplans.some(
                              (p) => String(p.level).trim() === String(opt.value).trim()
                            );
                            return (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} {hasPlan ? " 📐 (Floorplan Available)" : ""}
                              </option>
                            );
                          })}
                          {formData.level &&
                            !levelOptions.some((opt) => String(opt.value) === String(formData.level)) && (
                              <option value={formData.level}>
                                {formData.level} (Current)
                              </option>
                          )}
                        </select>
                      </div>
                    )}

                    {/* Available floorplans quick badges */}
                    {parentFloorplans.length > 0 && (
                      <div className="mt-2 p-2 bg-neutral-50 radius-6 border d-flex align-items-center gap-2 flex-wrap">
                        <span className="text-xxs fw-semibold text-secondary-light d-flex align-items-center gap-1">
                          <Icon icon="solar:layers-linear" />
                          Floorplans in Main Location:
                        </span>
                        {parentFloorplans.map((p) => {
                          const isSel = String(formData.level).trim() === String(p.level).trim();
                          return (
                            <button
                              key={p.id || p.level}
                              type="button"
                              onClick={() => setFormData({ ...formData, level: String(p.level) })}
                              className={`badge py-1 px-8 radius-4 border cursor-pointer ${
                                isSel
                                  ? "bg-primary-600 text-white border-primary-600"
                                  : "bg-white text-secondary-light border-neutral-300"
                              }`}
                              style={{ cursor: "pointer" }}
                              title={`Select Level ${p.level} (Floorplan available)`}
                            >
                              Level {p.level} {isSel ? "✓" : ""}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Context info under dropdown */}
                    {activeParentLocation?.building_levels_above_ground || activeParentLocation?.building_levels_below_ground ? (
                      <span className="text-xs text-secondary-light mt-1 d-block">
                        Levels from <strong>{activeParentLocation.name || activeParentLocation.location_name || "parent"}</strong> (
                        {activeParentLocation.building_levels_above_ground ? `${activeParentLocation.building_levels_above_ground} above ground` : ""}
                        {activeParentLocation.building_levels_above_ground && activeParentLocation.building_levels_below_ground ? ", " : ""}
                        {activeParentLocation.building_levels_below_ground ? `${activeParentLocation.building_levels_below_ground} below ground` : ""}
                        )
                      </span>
                    ) : activeParentLocation ? (
                      <span className="text-xs text-secondary-light mt-1 d-block">
                        Parent has no levels set yet. Showing standard range.
                      </span>
                    ) : (
                      <span className="text-xs text-warning-main mt-1 d-block">
                        Select a parent location above to load its building levels.
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <div className="col-md-12">
                    <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <label className="form-label text-xs fw-semibold text-dark mb-0">
                          Description & Internal Notes
                        </label>
                        <span className="badge bg-primary-50 text-primary-600 border border-primary-200 text-xxs px-2 py-0.5 rounded-pill d-inline-flex align-items-center gap-1">
                          <Icon icon="solar:magic-stick-3-bold" />
                          Gemini AI
                        </span>
                      </div>

                      {/* AI Action Buttons */}
                      <div className="d-flex align-items-center flex-wrap gap-2 position-relative">
                        {previousDescription !== null && (
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-neutral text-secondary-light py-1 px-8 radius-6 text-xxs d-inline-flex align-items-center gap-1"
                            onClick={() => {
                              const reverted = previousDescription;
                              setPreviousDescription(formData.description);
                              setFormData(prev => ({ ...prev, description: reverted }));
                              showToast('info', 'Reverted description');
                            }}
                            title="Undo / Revert to previous text"
                          >
                            <Icon icon="solar:undo-left-round-linear" />
                            <span>Revert</span>
                          </button>
                        )}

                        <button
                          type="button"
                          className="btn btn-xs btn-primary-600 text-white py-1 px-10 radius-6 text-xxs d-inline-flex align-items-center gap-1 fw-semibold shadow-xs"
                          disabled={aiLoading}
                          onClick={() => handleAiDescription('generate')}
                          title="Generate description automatically from sublocation & parent data"
                        >
                          {aiLoading && aiAction === 'generate' ? (
                            <>
                              <Icon icon="line-md:loading-twotone-loop" className="me-1" />
                              <span>Drafting...</span>
                            </>
                          ) : (
                            <>
                              <Icon icon="solar:stars-minimalistic-bold" />
                              <span>Generate with Gemini</span>
                            </>
                          )}
                        </button>

                        {formData.description?.trim() && (
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-primary text-primary-600 py-1 px-8 radius-6 text-xxs d-inline-flex align-items-center gap-1 fw-medium"
                            disabled={aiLoading}
                            onClick={() => handleAiDescription('polish')}
                            title="Improve tone, flow, and grammar of current description"
                          >
                            {aiLoading && aiAction === 'polish' ? (
                              <>
                                <Icon icon="line-md:loading-twotone-loop" className="me-1" />
                                <span>Polishing...</span>
                              </>
                            ) : (
                              <>
                                <Icon icon="solar:magic-stick-linear" />
                                <span>Polish</span>
                              </>
                            )}
                          </button>
                        )}

                        {/* More AI Tools Dropdown Menu */}
                        <div className="position-relative">
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-neutral text-secondary-light py-1 px-8 radius-6 text-xxs d-inline-flex align-items-center gap-1"
                            onClick={() => setShowAiMenu(prev => !prev)}
                            disabled={aiLoading}
                            title="More AI options (shorten, translate)"
                          >
                            <Icon icon="solar:menu-dots-bold" />
                            <span>More</span>
                          </button>

                          {showAiMenu && (
                            <>
                              <div
                                className="position-fixed top-0 start-0 w-100 h-100"
                                style={{ zIndex: 1050 }}
                                onClick={() => setShowAiMenu(false)}
                              />
                              <div
                                className="position-absolute end-0 top-100 mt-1 bg-white border radius-8 shadow-lg py-2"
                                style={{ zIndex: 1055, minWidth: "220px" }}
                              >
                                <div className="px-3 py-1 text-xs text-muted fw-semibold border-bottom mb-1">
                                  Gemini Actions
                                </div>
                                <button
                                  type="button"
                                  className="w-100 text-start px-3 py-1.5 border-0 bg-transparent text-xs d-flex align-items-center gap-2 hover-bg-neutral-50"
                                  disabled={!formData.description?.trim() || aiLoading}
                                  onClick={() => {
                                    setShowAiMenu(false);
                                    handleAiDescription('shorten');
                                  }}
                                >
                                  <Icon icon="solar:minimize-square-linear" className="text-warning-600 text-sm" />
                                  <div>
                                    <div className="fw-medium">Make Shorter / Punchy</div>
                                    <small className="text-muted">Condense to 2-3 sentences</small>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  className="w-100 text-start px-3 py-1.5 border-0 bg-transparent text-xs d-flex align-items-center gap-2 hover-bg-neutral-50"
                                  disabled={!formData.description?.trim() || aiLoading}
                                  onClick={() => {
                                    setShowAiMenu(false);
                                    handleAiDescription('translate_en');
                                  }}
                                >
                                  <Icon icon="solar:global-linear" className="text-info-600 text-sm" />
                                  <div>
                                    <div className="fw-medium">Translate to English</div>
                                    <small className="text-muted">High-quality translation</small>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  className="w-100 text-start px-3 py-1.5 border-0 bg-transparent text-xs d-flex align-items-center gap-2 hover-bg-neutral-50"
                                  disabled={!formData.description?.trim() || aiLoading}
                                  onClick={() => {
                                    setShowAiMenu(false);
                                    handleAiDescription('translate_de');
                                  }}
                                >
                                  <Icon icon="solar:global-linear" className="text-success-600 text-sm" />
                                  <div>
                                    <div className="fw-medium">Translate to German</div>
                                    <small className="text-muted">Deutsche Übersetzung</small>
                                  </div>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <textarea
                      className="form-control radius-8 text-xs"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />

                    {aiLoading && (
                      <div className="d-flex align-items-center gap-2 mt-2 text-primary-600 text-xxs bg-primary-50 px-3 py-2 radius-6 border border-primary-100">
                        <Icon icon="line-md:loading-twotone-loop" className="text-sm" />
                        <span>Gemini is composing sublocation description...</span>
                      </div>
                    )}
                  </div>


                  {/* Walk Through Navigation */}
                  <div className="col-12 mt-20 pt-16 border-top d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-primary px-16 py-8 radius-8 text-xs d-inline-flex align-items-center gap-2"
                      onClick={() => setActiveTab("Location & Pin")}
                    >
                      <span>Next: Location & Pin</span>
                      <Icon icon="solar:arrow-right-linear" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: LOCATION & PIN */}
            {activeTab === "Location & Pin" && (
              <div className="card border radius-12 p-24 shadow-none">
                <div className="border-bottom pb-16 mb-20 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <h6 className="text-md fw-bold mb-1">Map Position & Exact Coordinates</h6>
                    <p className="text-xs text-secondary-light mb-0">
                      Click anywhere on the map or drag the pin to set the exact location for this unit inside the parent premises.
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {parentLocation?.lat && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-primary radius-6 d-flex align-items-center gap-1 py-1 px-10 text-xs"
                        onClick={handleSnapToParent}
                        title="Snap pin to parent location coordinates"
                      >
                        <Icon icon="solar:pin-bold" />
                        <span>Center on Parent Business</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Physical Address Section */}
                <div className="border radius-8 p-16 bg-neutral-50 mb-16">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-12">
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon="solar:map-point-linear" className="text-primary-600 text-sm" />
                      <span className="text-xs fw-bold text-dark">Physical Address (Optional)</span>
                    </div>
                    {parentLocation && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-secondary radius-6 d-flex align-items-center gap-1 py-1 px-8 text-xxs"
                        onClick={handleAdoptParentAddress}
                        title="Copy Street, House No, Postal Code and City from Parent Business"
                      >
                        <Icon icon="solar:copy-linear" />
                        <span>Copy from Parent Business</span>
                      </button>
                    )}
                  </div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label text-xxs fw-semibold text-secondary-light mb-1">Street Address</label>
                      <input
                        type="text"
                        className="form-control form-control-sm radius-6 text-xs"
                        value={formData.street_address}
                        onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label text-xxs fw-semibold text-secondary-light mb-1">House No.</label>
                      <input
                        type="text"
                        className="form-control form-control-sm radius-6 text-xs"
                        value={formData.house_number}
                        onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-xxs fw-semibold text-secondary-light mb-1">Postal Code</label>
                      <input
                        type="text"
                        className="form-control form-control-sm radius-6 text-xs"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-xxs fw-semibold text-secondary-light mb-1">City</label>
                      <input
                        type="text"
                        className="form-control form-control-sm radius-6 text-xs"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-xxs fw-semibold text-secondary-light mb-1">Country</label>
                      <input
                        type="text"
                        className="form-control form-control-sm radius-6 text-xs"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Coordinates Bar & Floorplan Controls */}
                <div className="bg-neutral-50 p-12 radius-8 border mb-16 d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="d-flex align-items-center gap-1">
                      <span className="badge bg-neutral-200 text-secondary-light font-monospace py-1 px-1 text-xxs">LAT</span>
                      <input
                        type="number"
                        step="any"
                        className="form-control form-control-sm font-monospace text-xs"
                        style={{ width: "130px" }}
                        value={formData.lat}
                        onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <span className="badge bg-neutral-200 text-secondary-light font-monospace py-1 px-1 text-xxs">LNG</span>
                      <input
                        type="number"
                        step="any"
                        className="form-control form-control-sm font-monospace text-xs"
                        style={{ width: "130px" }}
                        value={formData.lng}
                        onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-1 ms-2">
                      <span className="text-xs fw-semibold text-secondary-light">Level:</span>
                      <select
                        className="form-select form-select-sm radius-6 text-xs py-1"
                        style={{ width: "auto", minWidth: "150px" }}
                        value={formData.level || ""}
                        onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                      >
                        <option value="">-- Select Level --</option>
                        {levelOptions.map((opt) => {
                          const hasPlan = parentFloorplans.some(
                            (p) => String(p.level).trim() === String(opt.value).trim()
                          );
                          return (
                            <option key={opt.value} value={opt.value}>
                              {opt.label} {hasPlan ? " 📐 (Floorplan)" : ""}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {activeFloorplan ? (
                      <>
                        <button
                          type="button"
                          className={`btn btn-xs py-1 px-8 radius-6 d-flex align-items-center gap-1 ${
                            showFloorplanOverlay ? "btn-primary text-white" : "btn-outline-secondary"
                          }`}
                          onClick={() => setShowFloorplanOverlay(!showFloorplanOverlay)}
                          title="Toggle level floorplan overlay on map"
                        >
                          <Icon icon="solar:layers-linear" className="text-xs" />
                          <span>Level {formData.level} Floorplan: {showFloorplanOverlay ? "Visible" : "Hidden"}</span>
                        </button>

                        <button
                          type="button"
                          className="btn btn-xs btn-outline-secondary py-1 px-8 radius-6 d-flex align-items-center gap-1"
                          onClick={() => handleFitFloorplan(pinMapInstance)}
                          title="Zoom & fit map to floorplan"
                        >
                          <Icon icon="solar:maximize-square-linear" className="text-xs" />
                          <span>Fit Floorplan</span>
                        </button>

                        <div className="d-flex align-items-center gap-1 text-xs">
                          <span className="text-secondary-light">Opacity:</span>
                          <select
                            className="form-select form-select-sm text-xs py-0 px-1"
                            style={{ width: "70px", height: "26px" }}
                            value={floorplanOpacity}
                            onChange={(e) => setFloorplanOpacity(parseFloat(e.target.value))}
                          >
                            <option value={1}>100%</option>
                            <option value={0.85}>85%</option>
                            <option value={0.65}>65%</option>
                            <option value={0.45}>45%</option>
                          </select>
                        </div>
                      </>
                    ) : (
                      formData.level && (
                        <span className="badge bg-neutral-100 text-secondary-light py-1 px-2 text-xxs">
                          No floorplan for Level {formData.level}
                        </span>
                      )
                    )}

                    {parentLocation && (
                      <div className="d-flex align-items-center gap-2 text-xs text-secondary-light ms-2">
                        <span className="w-8-px h-8-px radius-circle bg-primary-600 d-inline-block"></span>
                        <span>Parent Boundary: {parentOutlinePolygons.length > 0 ? "Displayed on Map" : "No outline set"}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Standard Google Map without labels */}
                <div className="border radius-12 overflow-hidden shadow-sm" style={{ height: "550px", position: "relative" }}>
                  {isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={mapCenter}
                      zoom={19}
                      mapTypeId="roadmap"
                      onClick={handlePinMapClick}
                      onLoad={(map) => {
                        pinMapRef.current = map;
                        setPinMapInstance(map);
                        try {
                          map.setTilt(0);
                          map.setMapTypeId("roadmap");
                        } catch (e) {}
                      }}
                      options={{
                        mapTypeId: "roadmap",
                        tilt: 0,
                        rotateControl: false,
                        zoomControl: true,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: true,
                        styles: [
                          {
                            featureType: "all",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }],
                          },
                        ],
                      }}
                    >
                      {/* Floorplan Overlay for selected level */}
                      <MapFloorplanOverlay
                        map={pinMapInstance}
                        floorplan={activeFloorplan}
                        visible={showFloorplanOverlay}
                        opacity={floorplanOpacity}
                      />

                      {/* Parent Main Location Boundary Overlay (Reference Guide) */}
                      {parentOutlinePolygons.map((poly, idx) => (
                        <PolygonF
                          key={`parent-poly-${idx}`}
                          paths={poly}
                          options={{
                            fillColor: "#0d6efd",
                            fillOpacity: 0.12,
                            strokeColor: "#0d6efd",
                            strokeOpacity: 0.85,
                            strokeWeight: 2,
                            clickable: false,
                            zIndex: 1,
                          }}
                        />
                      ))}

                      {/* Sub-Location Pin */}
                      {formData.lat && formData.lng && (
                        <MarkerF
                          position={{
                            lat: Number(formData.lat),
                            lng: Number(formData.lng),
                          }}
                          draggable={true}
                          onDragEnd={handleMarkerDragEnd}
                          title={`${formData.name || "Sub Location"} (Drag to adjust)`}
                          zIndex={10}
                        />
                      )}
                    </GoogleMap>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 bg-neutral-100">
                      <div className="spinner-border text-primary" role="status" />
                    </div>
                  )}
                </div>

                <div className="d-flex align-items-center justify-content-between mt-12 text-xs text-secondary-light">
                  <div className="d-flex align-items-center gap-2">
                    <Icon icon="solar:info-circle-linear" className="text-primary-600" />
                    <span>Blue polygon shows the parent building outline. Click or drag the marker to pinpoint the exact unit.</span>
                  </div>
                  {formData.lat && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 text-primary-600 text-decoration-none text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(`${formData.lat}, ${formData.lng}`);
                        showToast("success", "Coordinates copied to clipboard!");
                      }}
                    >
                      Copy Coordinates
                    </button>
                  )}
                </div>

                {/* Walk Through Navigation */}
                <div className="mt-20 pt-16 border-top d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-16 py-8 radius-8 text-xs d-inline-flex align-items-center gap-2"
                    onClick={() => setActiveTab("Identity")}
                  >
                    <Icon icon="solar:arrow-left-linear" />
                    <span>Prev: Identity</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary px-16 py-8 radius-8 text-xs d-inline-flex align-items-center gap-2"
                    onClick={() => setActiveTab("Building Outline")}
                  >
                    <span>Next: Building Outline (Optional)</span>
                    <Icon icon="solar:arrow-right-linear" />
                  </button>
                </div>
              </div>
            )}

            {/* TAB 3: BUILDING OUTLINE */}
            {activeTab === "Building Outline" && (
              <div className="card border radius-12 p-24 shadow-none">
                <div className="border-bottom pb-16 mb-20 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h6 className="text-md fw-bold mb-0">Unit Outline Boundaries</h6>
                      <span className="badge bg-neutral-100 text-secondary-light px-8 py-2 radius-4 text-xs">Optional</span>
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                      Draw or refine the exact polygon boundary for this sub-location (optional). The parent building outline is displayed in the background for alignment. You can save with or without boundaries.
                    </p>
                  </div>
                </div>

                <div className="row g-4">
                  {/* Left Column: Coordinates Table & Nudge Controls */}
                  <div className="col-xl-5 col-lg-5">
                    <div className="border radius-12 p-16 bg-white h-100 d-flex flex-column shadow-sm">
                      <div className="d-flex align-items-center justify-content-between mb-12">
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-primary-100 text-primary-600 radius-4 px-8 py-4 text-xs fw-semibold">
                            {currentPoints.length} Vertices
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {currentPoints.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-danger radius-4 py-1 px-8 text-xs d-flex align-items-center gap-1"
                              onClick={handleClearBoundary}
                            >
                              <Icon icon="solar:trash-bin-trash-linear" />
                              <span>Clear</span>
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-xs btn-primary radius-4 py-1 px-10 text-xs d-flex align-items-center gap-1"
                            onClick={handleSaveOutline}
                            disabled={saveLoading}
                          >
                            <Icon icon="solar:diskette-bold" />
                            <span>Save Outline</span>
                          </button>
                        </div>
                      </div>

                      {/* Multi-Polygon Selector & Add Shape */}
                      {activePolys.length > 0 && (
                        <div className="d-flex align-items-center gap-2 mb-12 overflow-x-auto pb-1 flex-nowrap" style={{ scrollbarWidth: "thin" }}>
                          {activePolys.map((poly, pIdx) => {
                            const isPolyActive = activeIndex === pIdx;
                            return (
                              <button
                                key={pIdx}
                                type="button"
                                className={`btn btn-sm text-nowrap flex-shrink-0 d-inline-flex align-items-center justify-content-center gap-1 px-10 radius-6 text-xs fw-semibold ${
                                  isPolyActive
                                    ? "text-white shadow-xs"
                                    : "btn-outline-primary bg-white text-primary-600 border"
                                }`}
                                style={{
                                  height: "34px",
                                  minHeight: "34px",
                                  maxHeight: "34px",
                                  lineHeight: "1",
                                  whiteSpace: "nowrap",
                                  backgroundColor: isPolyActive ? "#f97316" : "#ffffff",
                                  borderColor: isPolyActive ? "#ea580c" : "#bfdbfe",
                                  color: isPolyActive ? "#ffffff" : "#2563eb",
                                  flexShrink: 0
                                }}
                                onClick={() => {
                                  setActivePolyIndex(pIdx);
                                  setSelectedVertexIndex(0);
                                }}
                              >
                                <span
                                  className="radius-circle d-inline-block flex-shrink-0"
                                  style={{
                                    width: "7px",
                                    height: "7px",
                                    backgroundColor: isPolyActive ? "#ffffff" : "#0d6efd"
                                  }}
                                />
                                <span>Shape {pIdx + 1}</span>
                                <span className="opacity-75 font-monospace">({poly.length} pts)</span>
                              </button>
                            );
                          })}

                          {/* Listview Add Shape Button */}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary text-nowrap flex-shrink-0 d-inline-flex align-items-center justify-content-center gap-1 px-10 radius-6 text-xs fw-semibold border-dashed"
                            style={{
                              height: "34px",
                              minHeight: "34px",
                              maxHeight: "34px",
                              lineHeight: "1",
                              whiteSpace: "nowrap",
                              flexShrink: 0
                            }}
                            onClick={handleAddShape}
                            title="Add an additional shape"
                          >
                            <Icon icon="solar:add-circle-linear" style={{ fontSize: "15px" }} />
                            <span>+ Add Shape</span>
                          </button>
                        </div>
                      )}

                      {/* Step Adjuster Controls */}
                      <div className="d-flex align-items-center mb-12 bg-neutral-50 p-8 radius-8 border flex-wrap gap-2">
                        <span className="text-xs fw-semibold text-secondary-light">Nudge Step:</span>
                        <div className="d-flex align-items-center gap-1">
                          {[
                            { label: "±1m", value: 0.00001, title: "±0.00001 deg (~1.1m)" },
                            { label: "±5m", value: 0.00005, title: "±0.00005 deg (~5.5m)" },
                            { label: "±10m", value: 0.0001, title: "±0.00010 deg (~11m)" },
                            { label: "±10cm", value: 0.000001, title: "±0.000001 deg (~11cm)" },
                          ].map((stepOpt) => (
                            <button
                              key={stepOpt.value}
                              type="button"
                              title={stepOpt.title}
                              className={`btn btn-xs py-1 px-8 radius-4 text-xs fw-medium ${
                                coordStep === stepOpt.value
                                  ? "btn-primary shadow-xs"
                                  : "btn-outline-neutral text-secondary-light bg-white border"
                              }`}
                              onClick={() => setCoordStep(stepOpt.value)}
                            >
                              {stepOpt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Vertices Coordinates Table */}
                      {currentPoints.length > 0 ? (
                        <div className="table-responsive border radius-8" style={{ maxHeight: "360px", overflowY: "auto" }}>
                          <table className="table table-sm table-hover align-middle mb-0 text-xs">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th className="text-center py-2 px-1 text-secondary-light fw-semibold" style={{ width: "38px" }}>
                                  #
                                </th>
                                <th className="py-2 px-2 text-secondary-light fw-semibold">Coordinates (Lat / Lng)</th>
                                <th className="text-center py-2 px-1 text-secondary-light fw-semibold" style={{ width: "48px" }}>
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentPoints.map((pt, ptIdx) => {
                                const isSelected = selectedVertexIndex === ptIdx;
                                return (
                                  <tr
                                    key={`pt-${ptIdx}`}
                                    className={isSelected ? "table-primary" : ""}
                                    onClick={() => {
                                      setSelectedVertexIndex(ptIdx);
                                      if (outlineMapRef.current && pt.lat && pt.lng) {
                                        outlineMapRef.current.panTo({ lat: Number(pt.lat), lng: Number(pt.lng) });
                                      }
                                    }}
                                    style={{ cursor: "pointer", transition: "background-color 0.15s ease" }}
                                  >
                                    <td className="text-center py-2 px-1 fw-bold align-middle">
                                      <span
                                        className={`badge radius-4 px-2 py-1 ${
                                          isSelected ? "bg-primary text-white" : "bg-neutral-100 text-dark"
                                        }`}
                                      >
                                        {ptIdx + 1}
                                      </span>
                                    </td>
                                    <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                                      {/* Latitude Row */}
                                      <div className="d-flex align-items-center gap-1 mb-1">
                                        <span
                                          className={`badge font-monospace py-1 px-1 fw-semibold text-center ${
                                            isSelected ? "bg-primary-subtle text-primary-700" : "bg-neutral-200 text-secondary-light"
                                          }`}
                                          style={{ width: "32px", fontSize: "9px" }}
                                        >
                                          LAT
                                        </span>
                                        <div className="input-group input-group-sm flex-nowrap flex-grow-1">
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                            style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                            title={`Decrease latitude by ${coordStep}`}
                                            onClick={() => adjustCoordinate(activeIndex, ptIdx, "lat", -coordStep)}
                                          >
                                            -
                                          </button>
                                          <input
                                            type="number"
                                            step="any"
                                            className="form-control form-control-sm font-monospace text-center py-0 px-1 text-xs"
                                            style={{ height: "26px" }}
                                            value={pt.lat}
                                            onChange={(e) => handleCoordinateChange(activeIndex, ptIdx, "lat", e.target.value)}
                                          />
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                            style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                            title={`Increase latitude by ${coordStep}`}
                                            onClick={() => adjustCoordinate(activeIndex, ptIdx, "lat", coordStep)}
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>

                                      {/* Longitude Row */}
                                      <div className="d-flex align-items-center gap-1">
                                        <span
                                          className={`badge font-monospace py-1 px-1 fw-semibold text-center ${
                                            isSelected ? "bg-primary-subtle text-primary-700" : "bg-neutral-200 text-secondary-light"
                                          }`}
                                          style={{ width: "32px", fontSize: "9px" }}
                                        >
                                          LNG
                                        </span>
                                        <div className="input-group input-group-sm flex-nowrap flex-grow-1">
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                            style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                            title={`Decrease longitude by ${coordStep}`}
                                            onClick={() => adjustCoordinate(activeIndex, ptIdx, "lng", -coordStep)}
                                          >
                                            -
                                          </button>
                                          <input
                                            type="number"
                                            step="any"
                                            className="form-control form-control-sm font-monospace text-center py-0 px-1 text-xs"
                                            style={{ height: "26px" }}
                                            value={pt.lng}
                                            onChange={(e) => handleCoordinateChange(activeIndex, ptIdx, "lng", e.target.value)}
                                          />
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                            style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                            title={`Increase longitude by ${coordStep}`}
                                            onClick={() => adjustCoordinate(activeIndex, ptIdx, "lng", coordStep)}
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="text-center py-2 px-1 align-middle" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-outline-danger p-0 d-inline-flex align-items-center justify-content-center radius-4"
                                        style={{ width: "24px", height: "24px" }}
                                        title="Delete vertex"
                                        onClick={() => handleRemovePoint(activeIndex, ptIdx)}
                                      >
                                        <Icon icon="solar:trash-bin-trash-linear" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="p-20 radius-8 bg-neutral-50 border text-center my-8">
                          <div className="w-44-px h-44-px bg-primary-50 text-primary-600 radius-circle d-flex align-items-center justify-content-center mx-auto mb-10">
                            <Icon icon="tabler:polygon" className="text-2xl" />
                          </div>
                          <h6 className="fw-semibold text-dark mb-4 text-sm">No Unit Outline Yet</h6>
                          <p className="text-xs text-secondary-light mb-12">
                            Use the polygon draw tool on the map, or create a starter square around the unit pin.
                          </p>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary radius-6 d-inline-flex align-items-center gap-2 py-1 px-12 text-xs"
                            onClick={handleAddStarterOutline}
                          >
                            <Icon icon="solar:square-academic-cap-2-bold" />
                            <span>Create Starter Outline Around Pin</span>
                          </button>
                        </div>
                      )}

                      {/* Raw JSON toggle */}
                      <div className="d-flex align-items-center justify-content-end mt-12 pt-8 border-top">
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-secondary-light text-decoration-none p-0 text-xs"
                          onClick={() => setShowJsonRaw((prev) => !prev)}
                        >
                          <Icon icon={showJsonRaw ? "solar:eye-closed-linear" : "solar:eye-linear"} className="me-1" />
                          {showJsonRaw ? "Hide Raw JSON" : "View Raw JSON"}
                        </button>
                      </div>

                      {showJsonRaw && (
                        <div className="mt-10">
                          <textarea
                            className="form-control radius-8 bg-neutral-50 text-secondary-light"
                            readOnly
                            rows={4}
                            style={{ fontFamily: "monospace", fontSize: 10 }}
                            value={formData.geometrics_outline || ""}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Standard Roadmap Map with DrawTools */}
                  <div className="col-xl-7 col-lg-7">
                    <div className="border radius-12 overflow-hidden shadow-sm h-100 d-flex flex-column" style={{ minHeight: "580px" }}>
                      {/* Map Toolbar */}
                      <div className="d-flex align-items-center justify-content-between flex-wrap px-16 py-8 bg-white border-bottom gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div className="d-flex align-items-center gap-1">
                            <span className="text-xs fw-semibold text-secondary-light">Level:</span>
                            <select
                              className="form-select form-select-sm radius-6 text-xs py-1"
                              style={{ width: "auto", minWidth: "140px" }}
                              value={formData.level || ""}
                              onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                            >
                              <option value="">-- Select Level --</option>
                              {levelOptions.map((opt) => {
                                const hasPlan = parentFloorplans.some(
                                  (p) => String(p.level).trim() === String(opt.value).trim()
                                );
                                return (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label} {hasPlan ? " 📐 (Floorplan)" : ""}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {activeFloorplan ? (
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                              <button
                                type="button"
                                className={`btn btn-xs py-1 px-8 radius-4 d-flex align-items-center gap-1 ${
                                  showFloorplanOverlay ? "btn-primary text-white" : "btn-outline-secondary"
                                }`}
                                onClick={() => setShowFloorplanOverlay(!showFloorplanOverlay)}
                                title="Toggle floorplan overlay visibility"
                              >
                                <Icon icon="solar:layers-linear" className="text-xs" />
                                <span>Floorplan: {showFloorplanOverlay ? "Visible" : "Hidden"}</span>
                              </button>

                              <button
                                type="button"
                                className="btn btn-xs btn-outline-secondary py-1 px-8 radius-4 d-flex align-items-center gap-1"
                                onClick={() => handleFitFloorplan(outlineMapInstance)}
                                title="Zoom & fit map to floorplan bounds"
                              >
                                <Icon icon="solar:maximize-square-linear" className="text-xs" />
                                <span>Fit Floorplan</span>
                              </button>

                              <div className="d-flex align-items-center gap-1 text-xs">
                                <span className="text-secondary-light">Opacity:</span>
                                <select
                                  className="form-select form-select-sm text-xs py-0 px-1"
                                  style={{ width: "70px", height: "26px" }}
                                  value={floorplanOpacity}
                                  onChange={(e) => setFloorplanOpacity(parseFloat(e.target.value))}
                                >
                                  <option value={1}>100%</option>
                                  <option value={0.85}>85%</option>
                                  <option value={0.65}>65%</option>
                                  <option value={0.45}>45%</option>
                                </select>
                              </div>
                            </div>
                          ) : (
                            formData.level && (
                              <span className="badge bg-neutral-100 text-secondary-light py-1 px-2 text-xxs">
                                No floorplan for Level {formData.level}
                              </span>
                            )
                          )}
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={handleAddShape}
                            className="btn btn-xs btn-primary text-white py-1 px-10 radius-4 text-xs d-flex align-items-center gap-1 fw-semibold shadow-xs"
                            title="Add an additional shape to the map"
                          >
                            <Icon icon="solar:add-circle-bold" />
                            <span>+ Add Shape</span>
                          </button>
                          {formData.lat && formData.lng && (
                            <button
                              type="button"
                              onClick={() => {
                                if (outlineMapRef.current) {
                                  outlineMapRef.current.panTo({ lat: Number(formData.lat), lng: Number(formData.lng) });
                                  outlineMapRef.current.setZoom(19);
                                }
                              }}
                              className="btn btn-xs btn-outline-neutral text-secondary-light bg-white border py-1 px-8 radius-4 text-xs d-flex align-items-center gap-1"
                              title="Center on unit pin"
                            >
                              <Icon icon="solar:map-point-linear" />
                              <span>Center Pin</span>
                            </button>
                          )}
                          {currentPoints.length > 0 && (
                            <button
                              type="button"
                              onClick={() => fitMapToBoundary(formData.geometrics_outline)}
                              className="btn btn-xs btn-outline-neutral text-secondary-light bg-white border py-1 px-8 radius-4 text-xs d-flex align-items-center gap-1"
                              title="Fit map to unit outline"
                            >
                              <Icon icon="solar:maximize-square-linear" />
                              <span>Fit Outline</span>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleSaveOutline}
                            disabled={saveLoading}
                            className="btn btn-xs btn-primary py-1 px-12 radius-4 text-xs d-inline-flex align-items-center gap-1 fw-semibold"
                          >
                            <Icon icon="solar:diskette-bold" />
                            <span>Save Outline</span>
                          </button>
                        </div>
                      </div>

                      <div className="flex-grow-1 position-relative" style={{ minHeight: "530px" }}>
                        {isLoaded ? (
                          <GoogleMap
                            mapContainerStyle={{ width: "100%", height: "100%", minHeight: "530px" }}
                            center={mapCenter}
                            zoom={19}
                            mapTypeId="roadmap"
                            onLoad={onOutlineMapLoad}
                            options={{
                              mapTypeId: "roadmap",
                              tilt: 0,
                              rotateControl: false,
                              zoomControl: true,
                              mapTypeControl: false,
                              streetViewControl: false,
                              fullscreenControl: true,
                              styles: [
                                {
                                  featureType: "all",
                                  elementType: "labels",
                                  stylers: [{ visibility: "off" }],
                                },
                              ],
                            }}
                          >
                            {/* Floorplan Overlay for selected level */}
                            <MapFloorplanOverlay
                              map={outlineMapInstance}
                              floorplan={activeFloorplan}
                              visible={showFloorplanOverlay}
                              opacity={floorplanOpacity}
                            />

                            {/* Parent Main Location Boundary Overlay (Reference Guide) */}
                            {parentOutlinePolygons.map((poly, idx) => (
                              <PolygonF
                                key={`parent-poly-outline-${idx}`}
                                paths={poly}
                                options={{
                                  fillColor: "#64748b",
                                  fillOpacity: 0.08,
                                  strokeColor: "#3b82f6",
                                  strokeOpacity: 0.6,
                                  strokeWeight: 2,
                                  clickable: false,
                                  zIndex: 1,
                                }}
                              />
                            ))}

                            {/* Sublocation Outline Drawing Tools */}
                            <DrawTools
                              key={outlineDrawVersion}
                              data={formData.geometrics_outline}
                              onChange={(json) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  geometrics_outline: json,
                                }))
                              }
                              onVertexSelect={(polyIdx, vertIdx) => {
                                setActivePolyIndex(polyIdx);
                                setSelectedVertexIndex(vertIdx);
                              }}
                              activePolyIndex={activeIndex}
                            />
                          </GoogleMap>
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 bg-neutral-100">
                            <div className="spinner-border text-primary" role="status" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Walk Through Navigation */}
                <div className="mt-20 pt-16 border-top d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-16 py-8 radius-8 text-xs d-inline-flex align-items-center gap-2"
                    onClick={() => setActiveTab("Location & Pin")}
                  >
                    <Icon icon="solar:arrow-left-linear" />
                    <span>Prev: Location & Pin</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary px-16 py-8 radius-8 text-xs d-inline-flex align-items-center gap-2"
                    onClick={() => setActiveTab("Media & Files")}
                  >
                    <span>Next: Media & Files (Optional)</span>
                    <Icon icon="solar:arrow-right-linear" />
                  </button>
                </div>
              </div>
            )}

            {/* TAB 4: MEDIA & FILES */}
            {activeTab === "Media & Files" && (
              <div className="card border radius-12 p-24 shadow-none">
                <div className="border-bottom pb-16 mb-20 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h6 className="text-md fw-bold mb-0">Unit Media & Assets</h6>
                      <span className="badge bg-neutral-100 text-secondary-light px-8 py-2 radius-4 text-xs">Optional</span>
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                      Upload and manage photos, logos, floorplans, and documents specifically for this sub-location (optional).
                    </p>
                  </div>
                </div>

                {id ? (
                  <LocationMediaExplorer
                    locationId={id}
                    locationData={{
                      ...formData,
                      parent_location_name: parentLocation?.name,
                    }}
                    isSubLocation={true}
                  />
                ) : (
                  <div className="p-32 radius-12 bg-neutral-50 border text-center my-16">
                    <div className="w-56-px h-56-px bg-primary-50 text-primary-600 radius-circle d-flex align-items-center justify-content-center mx-auto mb-16">
                      <Icon icon="solar:gallery-wide-linear" className="text-3xl" />
                    </div>
                    <h6 className="fw-bold text-dark mb-6">Save Unit First to Enable Media Uploads</h6>
                    <p className="text-xs text-secondary-light mb-20" style={{ maxWidth: "420px", margin: "0 auto" }}>
                      Once the unit is saved with its parent location and identity details, you can upload high-resolution photos, floorplans, and documents directly to Google Cloud Storage.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary radius-8 px-20 py-10 text-xs d-inline-flex align-items-center gap-2"
                      onClick={handleSubmit}
                      disabled={saveLoading}
                    >
                      <Icon icon="solar:diskette-bold" />
                      <span>{saveLoading ? "Saving..." : "Save Unit & Continue to Media"}</span>
                    </button>
                  </div>
                )}

                {/* Walk Through Navigation */}
                <div className="mt-20 pt-16 border-top d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-16 py-8 radius-8 text-xs d-inline-flex align-items-center gap-2"
                    onClick={() => setActiveTab("Building Outline")}
                  >
                    <Icon icon="solar:arrow-left-linear" />
                    <span>Prev: Building Outline</span>
                  </button>
                  <div className="text-xs text-secondary-light">
                    {id ? "Media changes are saved automatically." : "Save the unit to upload media."}
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Floating Sticky Actions Bar & Walk Through Navigation */}
      <FloatingFormActions
        isDirty={hasFormChanged()}
        prevStep={prevTab}
        onPrev={prevTab ? () => setActiveTab(prevTab) : null}
        nextStep={nextTab}
        onNext={nextTab ? () => setActiveTab(nextTab) : null}
        cancelUrl="/locations-sub"
        cancelText="Discard"
        saveText={id ? "Update Unit" : "Save Unit"}
        loading={saveLoading}
        onSubmit={handleSubmit}
      />
    </form>
  </div>
  );
};

export default LocationsSubFormPage;